import { type ChangeEvent, type FC } from "react";

import { formatPrice } from "@jti/format-price";
import type { ICurrencyEnum } from "@jti/magento";
import { type GetEligibleCampaignsResponseType } from "@jti/rest-api-types";
import { CheckoutV2LoyaltyChoice } from "@jti/store/slices/checkoutV2Slice";
import { Tooltip, useTranslations } from "@jti/ui";

import { LoyaltyPointsBlock } from "../LoyaltyPointsBlock";
import { LoyaltyPointsDiscounts } from "../LoyaltyPointsDiscounts";

import styles from "./LoyaltyPointsToggle.module.scss";

type LoyaltyPointsToggleProps = {
  minVoucherCost: number;
  maxVoucherValue: number;
  setCollect: () => void;
  setRedeem: () => void;
  isRedeemAvailable: boolean;
  cartPoints: number;
  userPoints: number;
  currency: ICurrencyEnum;
  redemptionTabSymbol: string;
  loyaltyChoice: CheckoutV2LoyaltyChoice | undefined;
  vouchers: GetEligibleCampaignsResponseType[];
  isQueryCampaign: boolean;
  isLoyaltyError: boolean;
  loyaltyCampaignId: string;
  handleCampaignChange: (e: ChangeEvent<HTMLInputElement>) => void;
};

export const LoyaltyPointsToggle: FC<LoyaltyPointsToggleProps> = ({
  minVoucherCost,
  maxVoucherValue,
  setCollect,
  setRedeem,
  isRedeemAvailable,
  cartPoints,
  userPoints,
  currency,
  redemptionTabSymbol,
  loyaltyChoice,
  vouchers,
  isQueryCampaign,
  isLoyaltyError,
  loyaltyCampaignId,
  handleCampaignChange,
}) => {
  const { __ } = useTranslations();

  if (cartPoints <= 0 && (!minVoucherCost || userPoints < minVoucherCost)) {
    return null;
  }

  const infoText = __("loyalty.checkout.additionalInfo");
  const infoIconText = __("loyalty.checkout.additionalInfoIcon");

  return (
    <>
      <div className={styles.toggleWrapper}>
        <LoyaltyPointsBlock
          title={`+${cartPoints}`}
          onClick={setCollect}
          isActive={loyaltyChoice === CheckoutV2LoyaltyChoice.COLLECT}
          disabled={cartPoints <= 0 && userPoints <= 0}
          text={__("loyalty.pointsBlock.collect", [cartPoints])}
          dataTestid="checkoutCollectPoints"
        />
        <LoyaltyPointsBlock
          title={redemptionTabSymbol}
          onClick={setRedeem}
          isActive={loyaltyChoice === CheckoutV2LoyaltyChoice.REDEEM}
          text={__("loyalty.pointsBlock.redeem", [
            formatPrice({
              currency,
              value: maxVoucherValue,
            }),
          ])}
          disabled={!isRedeemAvailable}
          dataTestid="checkoutRedeemPoints"
        />
      </div>
      {loyaltyChoice === CheckoutV2LoyaltyChoice.REDEEM && (
        <div className={styles.discountsWrapper} data-testid="checkoutRedeem">
          <div className={styles.discountsHeader}>{__("loyalty.checkout.discountSelect")}</div>
          <LoyaltyPointsDiscounts
            currency={currency}
            vouchers={vouchers}
            isQueryCampaign={isQueryCampaign}
            isLoyaltyError={isLoyaltyError}
            loyaltyCampaignId={loyaltyCampaignId}
            handleCampaignChange={handleCampaignChange}
          />
          {infoIconText && infoIconText !== "loyalty.checkout.additionalInfoIcon" && (
            <div className={styles.additionalInfoWrapper}>
              {infoText && infoText !== "loyalty.checkout.additionalInfo" && (
                <Tooltip
                  dataTestId="redeemPopoverButton"
                  iconClassName={styles.discountsAdditionalInfoIcon}
                  content={<span data-testid="redeemPopoverAdditionalInfo">{infoText}</span>}
                />
              )}
              <span className={styles.discountsAdditionalInfo}>{infoIconText}</span>
            </div>
          )}
        </div>
      )}
    </>
  );
};
